import React, { useState } from "react";
import * as Immutable from "immutable";
import styled from "styled-components";
import { useStore } from "stores/connect";
import { EntityList, IfPermitted, PaginatedList, SearchForm, Spinner } from "components/common";
import { Button } from "components/bootstrap";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import ForwarderInputListItem from "./ForwarderInputListItem";
import ForwarderInputTypesStore from "../stores/ForwarderInputTypesStore";
import { ForwarderInputsActions } from "../stores/ForwarderInputsStore";
import InputForm from "../inputs/InputForm";
import { StyledSearchFormWrapper, StyledTitle } from "../wizard/StyledWizardComponents";
const StyledButton = styled(Button)(
  () => `
  margin-left: 5px;
`
);
const StyledFormWrapper = styled.div`
  margin-top: 10px;
`;
const ACTIONS = {
  NEW: "new",
  LIST: "list",
  EDIT: "edit"
};
const ForwarderInputsManagement = ({
  forwarder = void 0,
  inputs = [],
  inputProfile,
  enableEdit = false,
  pagination,
  onQueryChange,
  onInputChange = () => {
  },
  onActionChange = () => {
  }
}) => {
  const { total, page, perPage, query } = pagination;
  const [expandedInputDetails, setExpandedInputDetails] = useState(Immutable.Set());
  const { forwarderInputDescriptions: inputDescriptions, forwarderInputTypes: inputTypes } = useStore(ForwarderInputTypesStore);
  const [selectedInput, setSelectedInput] = useState(void 0);
  const [action, setAction] = useState(ACTIONS.LIST);
  const sendTelemetry = useSendTelemetry();
  const _toggleInputDetail = (id) => {
    let newSet;
    if (expandedInputDetails.contains(id)) {
      newSet = expandedInputDetails.delete(id);
    } else {
      newSet = expandedInputDetails.add(id);
    }
    setExpandedInputDetails(newSet);
  };
  const _showInputList = () => {
    setAction(ACTIONS.LIST);
    onActionChange(true);
  };
  const _handleInputCreate = (input) => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.FORWARDER.FORWARDER_INPUT_PROFILE_INPUT_CREATED, {
      app_pathname: "forwarder",
      app_section: "input-profile"
    });
    ForwarderInputsActions.create(inputProfile.id, input).then(() => {
      _showInputList();
      onInputChange();
    });
  };
  const _handleInputUpdate = (updatedInput) => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.FORWARDER.FORWARDER_INPUT_PROFILE_INPUT_UPDATED, {
      app_pathname: "forwarder",
      app_section: "input-profile"
    });
    if (!selectedInput) {
      throw new Error("No selected input when updating input. This should not happen at this stage.");
    }
    ForwarderInputsActions.update(inputProfile.id, selectedInput.id, updatedInput).then(() => {
      _showInputList();
      onInputChange();
    });
  };
  const _handleInputDelete = (input) => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.FORWARDER.FORWARDER_INPUT_PROFILE_INPUT_DELETED, {
      app_pathname: "forwarder",
      app_section: "input-profile"
    });
    if (window.confirm(`Are you sure you want to delete input ${input.title}? This action cannot be undone.`)) {
      ForwarderInputsActions.delete(inputProfile.id, input).then(() => {
        onInputChange();
      });
    }
  };
  const _handleSearch = (nextQuery) => onQueryChange(1, 10, nextQuery);
  const _handleSearchReset = () => onQueryChange();
  const _handlePaginationChange = (nextPage, nextPageSize) => {
    onQueryChange(nextPage, nextPageSize, query);
  };
  const _showEditForm = (input) => {
    setSelectedInput(input);
    setAction(ACTIONS.EDIT);
    onActionChange(false);
  };
  const _showCreateForm = () => {
    setAction(ACTIONS.NEW);
    onActionChange(false);
  };
  const _formatInput = (input) => {
    const { id: inputId, type: inputType } = input;
    return /* @__PURE__ */ React.createElement(
      ForwarderInputListItem,
      {
        key: inputId,
        forwarder,
        input,
        inputDescription: inputDescriptions[inputType],
        onToggleDetails: _toggleInputDetail,
        onDeleteInput: _handleInputDelete,
        onEditInput: _showEditForm,
        enableEdit,
        expanded: expandedInputDetails.contains(inputId)
      }
    );
  };
  const getInputsAction = () => {
    switch (action) {
      case ACTIONS.NEW:
        return /* @__PURE__ */ React.createElement(StyledFormWrapper, null, /* @__PURE__ */ React.createElement(StyledTitle, null, "Add Input to ", inputProfile.title), /* @__PURE__ */ React.createElement(
          InputForm,
          {
            action: "create",
            inputTypes,
            inputDescriptions,
            onCancel: _showInputList,
            onSubmit: _handleInputCreate
          }
        ));
      // @ts-expect-error fallthrough is on purpose
      case ACTIONS.EDIT:
        if (selectedInput) {
          return /* @__PURE__ */ React.createElement(StyledFormWrapper, null, /* @__PURE__ */ React.createElement(StyledTitle, null, "Edit Input ", selectedInput.title, /* @__PURE__ */ React.createElement("small", null, " (", inputTypes[selectedInput.type], ") "), "in ", inputProfile.title), /* @__PURE__ */ React.createElement(
            InputForm,
            {
              action: "edit",
              input: selectedInput,
              inputTypes,
              inputDescriptions,
              onCancel: _showInputList,
              onSubmit: _handleInputUpdate
            }
          ));
        }
      // eslint-disable-next-line no-fallthrough
      default:
        return /* @__PURE__ */ React.createElement(
          PaginatedList,
          {
            onChange: _handlePaginationChange,
            totalItems: total,
            showPageSizeSelect: false,
            activePage: page,
            pageSize: perPage,
            useQueryParameter: false
          },
          /* @__PURE__ */ React.createElement(StyledSearchFormWrapper, null, /* @__PURE__ */ React.createElement(
            SearchForm,
            {
              query,
              onSearch: _handleSearch,
              onReset: _handleSearchReset,
              placeholder: "Filter by title"
            },
            enableEdit ? /* @__PURE__ */ React.createElement(IfPermitted, { permissions: "forwarderinputs:create" }, /* @__PURE__ */ React.createElement(StyledButton, { onClick: _showCreateForm, bsStyle: "primary" }, /* @__PURE__ */ React.createElement("span", null, "Create input"))) : null
          )),
          /* @__PURE__ */ React.createElement(
            EntityList,
            {
              bsNoItemsStyle: "info",
              noItemsText: inputs.length <= 0 ? "There are no inputs configured for this Input Profile" : "No inputs match the filter",
              items: inputs.map((input) => _formatInput(input))
            }
          )
        );
    }
  };
  if (!inputDescriptions || !inputTypes) {
    return /* @__PURE__ */ React.createElement(Spinner, null);
  }
  return /* @__PURE__ */ React.createElement(React.Fragment, null, getInputsAction());
};
export default ForwarderInputsManagement;
